import * as React from 'react';
import { Search, X } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// SearchInput size variants using CVA
const searchInputVariants = cva(
  'flex items-center rounded-lg border border-gray-300 bg-white transition-all duration-200 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20',
  {
    variants: {
      size: {
        sm: 'h-8 px-2 text-sm',
        md: 'h-10 px-3 text-sm',
        lg: 'h-12 px-4 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const searchIconVariants = cva('text-gray-400 flex-shrink-0', {
  variants: {
    size: {
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const clearButtonVariants = cva(
  'text-gray-400 hover:text-gray-600 focus:outline-none focus:text-primary-600 transition-colors flex-shrink-0',
  {
    variants: {
      size: {
        sm: 'h-3.5 w-3.5',
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof searchInputVariants> {
  /** Current search value */
  value: string;
  /** Callback when search value changes (debounced if debounceMs > 0) */
  onValueChange: (value: string) => void;
  /** Debounce delay in milliseconds (default: 300, set to 0 to disable) */
  debounceMs?: number;
  /** Show clear button when value exists (default: true) */
  showClearButton?: boolean;
  /** Additional class name for the container */
  containerClassName?: string;
  /** Placeholder text (default: "Search...") */
  placeholder?: string;
}

/**
 * SearchInput Component with built-in debouncing
 * 
 * Features:
 * - Search icon on the left
 * - Clear button (X) on the right when value exists
 * - Configurable debounce delay (default 300ms)
 * - Size variants (sm, md, lg)
 * - Full keyboard support
 * - RTL-aware layout
 * 
 * @example
 * ```tsx
 * const [search, setSearch] = useState('');
 * 
 * <SearchInput
 *   value={search}
 *   onValueChange={setSearch}
 *   placeholder="Search templates..."
 *   debounceMs={300}
 * />
 * ```
 */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onValueChange,
      debounceMs = 300,
      showClearButton = true,
      size = 'md',
      containerClassName,
      placeholder = 'Search...',
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(value);
    const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current!);

    // Sync internal value with external value
    React.useEffect(() => {
      setInternalValue(value);
    }, [value]);

    // Handle input change with debouncing
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInternalValue(newValue);

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // If debounce is disabled, call immediately
      if (debounceMs <= 0) {
        onValueChange(newValue);
        return;
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, debounceMs);
    };

    // Clear search
    const handleClear = () => {
      setInternalValue('');
      onValueChange('');
      inputRef.current?.focus();
    };

    // Handle keyboard events
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape' && internalValue) {
        event.preventDefault();
        handleClear();
      }
    };

    // Cleanup on unmount
    React.useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    return (
      <div
        className={cn(
          searchInputVariants({ size }),
          disabled && 'opacity-50 cursor-not-allowed',
          containerClassName
        )}
      >
        {/* Search Icon */}
        <Search
          className={cn(searchIconVariants({ size }), 'mr-2 rtl:mr-0 rtl:ml-2')}
          aria-hidden="true"
        />

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={internalValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'flex-1 bg-transparent outline-none placeholder:text-gray-400',
            disabled && 'cursor-not-allowed',
            className
          )}
          aria-label={placeholder}
          {...props}
        />

        {/* Clear Button */}
        {showClearButton && internalValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(clearButtonVariants({ size }), 'ml-2 rtl:ml-0 rtl:mr-2')}
            aria-label="Clear search"
          >
            <X className="h-full w-full" />
          </button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export { searchInputVariants };
